import * as React from "react";
import { Box, Flex } from "@quandoo-dev/cookbook/lib/layout";
import { Body } from "@quandoo-dev/cookbook/lib/typography";
import Expander, { Expandable } from "@quandoo-dev/cookbook/lib/expandable";
import styled from "styled-components";

import SectionTitle from "./section-title";
import {
  prepareOpeningHours,
  OpeningHour,
  StructuredOpeningHours,
} from "../../utils/opening-hours";
import ArrowDown from "../../assets/arrow-down.inline.svg";
import useTranslation from "../../hooks/useTranslation";

interface IOpeningHours {
  openingHours: OpeningHour[];
  country: string;
}

interface DayOpenHoursProps {
  days: StructuredOpeningHours[];
  country: string;
}

// TODO: map it with Resources/translations

const List = styled.ul`
  list-style: none;
  padding: 12px 0;
  margin: 0;
`;

const DayOpenHours: React.FC<DayOpenHoursProps> = ({ days, country }) => {
  let week = [];

  const TEMPORARY_DAYS_MAPPING = {
    "1": useTranslation(country, "merchant.monday"),
    "2": useTranslation(country, "merchant.tuesday"),
    "3": useTranslation(country, "merchant.wednesday"),
    "4": useTranslation(country, "merchant.thursday"),
    "5": useTranslation(country, "merchant.friday"),
    "6": useTranslation(country, "merchant.saturday"),
    "7": useTranslation(country, "merchant.sunday"),
  };

  for (let i = 1; i < 8; i++) {
    const selected = days.find(element => element.day === String(i));
    const day = selected ? (
      <Flex key={i} mb="xs">
        <Box flex="1 1 33%">
          <Body fontWeight="semibold">{TEMPORARY_DAYS_MAPPING[i]}</Body>
        </Box>
        <Flex flex="1 1 64%">
          {selected.times.map(({ open, close }, index) => (
            <Box flex="1 1 0" key={index}>
              <Body key={index}>
                {open} - {close}
              </Body>
            </Box>
          ))}
        </Flex>
      </Flex>
    ) : (
      <Flex key={i} mb="xs">
        <Box flex="1 1 33%">
          <Body fontWeight="semibold">{TEMPORARY_DAYS_MAPPING[i]}</Body>
        </Box>
        <Flex flex="1 1 64%">
          <Box flex="1 1 0" key={i}>
            <Body key={i}>Closed</Body>
          </Box>
        </Flex>
      </Flex>
    );
    week.push(day);
  }

  return <List>{week}</List>;
};

interface CurrentOpenHoursProps {
  hours: StructuredOpeningHours;
  toggle: () => void;
  country: string;
}

const Wrapper = styled(Flex)`
  cursor: pointer;
`;

const CurrentOpenHours: React.FC<CurrentOpenHoursProps> = ({
  hours,
  toggle,
  country,
}) => {
  const nowOpenText = useTranslation(country, "post.opening_hours.now_open");

  return (
    <Wrapper mt="xs" onClick={() => toggle()}>
      <Box flex="1 1 33%">
        <Body fontWeight="semibold" color="success">
          {nowOpenText}
        </Body>
      </Box>
      <Flex flex="1 1 64%">
        {hours.times.map(({ open, close }, index) => (
          <Box flex="1 1 0" key={index}>
            <Body>
              {open} - {close}
            </Body>
          </Box>
        ))}
        <Box ml="-12px">
          <ArrowDown />
        </Box>
      </Flex>
    </Wrapper>
  );
};

const OpeningHours: React.FC<IOpeningHours> = ({ openingHours, country }) => {
  const preparedOpeningHours = prepareOpeningHours(openingHours);
  const openingHoursText = useTranslation(
    country,
    "post.merchant.opening_hours"
  );

  // TODO: calculate current time and choose that date
  const currentOpenHours: StructuredOpeningHours = preparedOpeningHours[0];

  return (
    <Box my="xl" maxWidth={{ _: "auto", md: "535px" }}>
      <SectionTitle
        letterSpacing="1px"
        fontSize="body.sm"
        fontWeight="semibold"
      >
        {openingHoursText}
      </SectionTitle>
      <Expander enabled={[true]}>
        {({ isOpen, toggle }) => (
          <>
            <CurrentOpenHours
              hours={currentOpenHours}
              toggle={toggle}
              country={country}
            />
            <Expandable>
              <DayOpenHours days={preparedOpeningHours} country={country} />
            </Expandable>
          </>
        )}
      </Expander>
    </Box>
  );
};

export default OpeningHours;
